import { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  setUsername,
  setUserIdStore,
  setClientId,
  setRoleName,
  setFileDetails,
  setUserStatus,
  setEDDStatus,
  setSurveyStatus,
  setForcedPassword,
  setAuthInitialized,
  setPendingTraining,
  setAvailableRoleList,
} from '../../Redux/store/userSlice';
import { useToast } from '../../Utils/toastContext';
import { setApplicationType } from '../../Redux/store/accountTypeSlice';
import { changeCardFalse } from '../../Redux/store/selectedCard';
import { setIndividualAccountType } from '../../Redux/store/onboardingFormSlice';
import { Data } from '@react-google-maps/api';
import { User, Shield, Briefcase, CheckCircle2 } from 'lucide-react';
import { debounce } from 'lodash';

const roleConfig = {
  agent: {
    label: 'Team Members',
    icon: User,
    bg: 'bg-blue-50',
    color: 'text-blue-600',
    border: 'border-blue-200',
  },
  agency: {
    label: 'Business Owner',
    icon: Briefcase,
    bg: 'bg-indigo-50',
    color: 'text-indigo-600',
    border: 'border-indigo-200',
  },
  agency_compliance: {
    label: 'Compliance Officer',
    icon: Shield,
    bg: 'bg-emerald-50',
    color: 'text-emerald-600',
    border: 'border-emerald-200',
  },
  customer: {
    label: 'Customer',
    icon: User,
    bg: 'bg-purple-50',
    color: 'text-purple-600',
    border: 'border-purple-200',
  },
};
const WebSocketComponent = ({ userId, socketToken }) => {
  const WS_URL = process.env.REACT_APP_WS_URL;
  const { showToast } = useToast();
  const tokenSentRef = useRef(false);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const isAuthenticatedRef = useRef(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loginResponse, setLoginResponse] = useState(null);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [showRoleModal, setShowRoleModal] = useState(false);

  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const getProfilePicture = async (token) => {
    try {
      const response = await GetAPIcalls('/api/v1/users/me', token);

      if (response.status == 200) {
        return response.data;
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const GetAPIcalls = async (url, token) => {
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const proceedAfterLogin = async (response, roleName) => {
    dispatch(setRoleName(roleName));

    dispatch(setUserIdStore(response.data.user_id));

    // 👉 continue your existing navigation logic here
    localStorage.setItem('lastActivity', Date.now().toString());
    localStorage.setItem('systemToken', response.data.session_id);
    dispatch(setAuthInitialized(false));
    dispatch(setUserStatus(response.data.status));
    dispatch(setForcedPassword(response.data.enforce_password_reset));

    //   setLoading(false);
    dispatch(changeCardFalse());
    const profileData = await getProfilePicture(response.data.access_token);
    let haspendingTrining = false;
    if (roleName === 'customer') {
      dispatch(setEDDStatus(profileData.has_pending_edd));
    }
    if (roleName === 'agency' || roleName === 'agency_compliance') {
      dispatch(setSurveyStatus(profileData.has_pending_survey));
    }
    if (roleName === 'agent' || roleName === 'agency_compliance') {
      dispatch(setPendingTraining(profileData.has_pending_training));
    }
    dispatch(setAuthInitialized(true));
    const savedRedirect = sessionStorage.getItem('redirectAfterLogin');
    if (savedRedirect) {
      console.log('saved url ' + savedRedirect);

      const { path, state } = JSON.parse(savedRedirect);
      // Prevent active users going back to onboarding
      if (response.data.status === 'active' && path === '/onboarding') {
        sessionStorage.removeItem('redirectAfterLogin');
        localStorage.setItem('sideOption', 1);
        navigate('/');
      } else {
        navigate(path, { state });
        sessionStorage.removeItem('redirectAfterLogin');
      }
    } else {
      console.log('saved url no ' + savedRedirect);

      if (response.data.status === 'active') {
        navigate('/');
      } else {
        console.log(
          'Navigating to onboarding due to userStatus:',
          response.data.status,
        );
        navigate('/onboarding');
      }
    }
  };
  const postSwitchAPI = async (roleid) => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    const apiUrl = `${BASE_URL}/api/v1/auth/acting-role/exchange`;
    const bodyData = {
      role_id: roleid,
    };

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));

        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleUpdateSwtich = useCallback(
    debounce(async (e, rolename, roleid) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        const response = await postSwitchAPI(roleid);
        if (response.status === 200) {
          proceedAfterLogin(loginResponse, rolename);
          localStorage.setItem('jwtToken', response.data.access_token);
          localStorage.setItem('refreshJwtToken', response.data.refresh_token);
        }
      } catch (error) {
        toastMessage = { message: error.message, type: 'error' };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000),
    [loginResponse], // Dependencies
  );
  useEffect(() => {
    const socket = new WebSocket(WS_URL);

    socket.onopen = () => {
      console.log('WebSocket connected');

      // ✅ ONLY THIS IS REQUIRED
      socket.send(
        JSON.stringify({
          token: socketToken,
        }),
      );
    };

    socket.onmessage = async (event) => {
      console.log('Raw WebSocket message received:', event.data);
      try {
        const data = JSON.parse(event.data);
        console.log('Parsed WebSocket data:', data);
        // 🔐 Step: Authentication confirmation
        if (data?.data?.status === 'authenticated') {
          console.log('Authenticated successfully');
          isAuthenticatedRef.current = true;
          return;
        }
        if (data?.type === 'error') {
          console.error('WS Error:', data?.data?.error);
          return;
        }
        if (!isAuthenticatedRef.current) {
          console.log('Blocked: not authenticated yet');
          return;
        }

        if (data.event === 'auth_code_verified') {
          // setLoading(true);
          if (data.data.last_name != null) {
            dispatch(
              setUsername(data.data.first_name + ' ' + data.data.last_name),
            );
          } else {
            dispatch(setUsername(data.data.first_name));
          }
          dispatch(setUserIdStore(data.data.user_id));
          setLoginResponse(data); // ✅ STORE IT HERE
          const roles = data.data.roles || [];
          localStorage.setItem('jwtToken', data.data.access_token);
          localStorage.setItem('refreshJwtToken', data.data.refresh_token);

          if (roles.length === 1) {
            dispatch(setRoleName(roles[0].name));
            dispatch(setAvailableRoleList(roles));
            proceedAfterLogin(data, roles[0].name);
          } else {
            setAvailableRoles(roles);
            dispatch(setAvailableRoleList(roles));
            setShowRoleModal(true);
            return; // STOP FLOW HERE until user selects role
          }
          // dispatch(setRoleName(data.data.roles[0].name));
          // //  dispatch(setRoleName("officer"));
          // localStorage.setItem("jwtToken", data.data.access_token);
          // localStorage.setItem("lastActivity", Date.now().toString());
          // localStorage.setItem("refreshJwtToken", data.data.refresh_token);
          // localStorage.setItem("systemToken", data.data.session_id);
          // dispatch(setApplicationType(data.data.account_type_id));
          // dispatch(setIndividualAccountType(data.data.account_type_id));
          // dispatch(setClientId(data.data.client_id));
          // dispatch(setUserStatus(data.data.status));
          // // if (data.data.enforce_password_reset) {
          // //   navigate("/forgot-password", {
          // //     state: {
          // //       from: location.pathname,
          // //       isForced: true,
          // //     },
          // //   });
          // // }
          // let survey = true;

          // dispatch(setUserStatus(data.data.status));
          // dispatch(setForcedPassword(data.data.enforce_password_reset));

          // setLoading(false);
          // dispatch(changeCardFalse());
          // const profileData = await getProfilePicture(data.data.access_token);
          // let haspendingTrining = false;
          // if (data.data.roles[0].name === "customer") {
          //   dispatch(setEDDStatus(profileData.has_pending_edd));
          // }
          // if (
          //   data.data.roles[0].name === "agency" ||
          //   data.data.roles[0].name === "agency_compliance"
          // ) {
          //   dispatch(setSurveyStatus(profileData.has_pending_survey));
          // }
          //    dispatch(setAuthInitialized(true));
          //           const savedRedirect = sessionStorage.getItem("redirectAfterLogin");
          //           if (savedRedirect) {
          //             console.log("saved url " + savedRedirect);

          //             const { path, state } = JSON.parse(savedRedirect);
          //             // Prevent active users going back to onboarding
          //             if (data.data.status === "active" && path === "/onboarding") {
          //               sessionStorage.removeItem("redirectAfterLogin");
          //               localStorage.setItem("sideOption", 1);
          //               navigate("/");
          //             } else {
          //               navigate(path, { state });
          //               sessionStorage.removeItem("redirectAfterLogin");
          //             }
          //           } else {
          //             console.log("saved url no " + savedRedirect);

          //             if (data.data.status === "active") {
          //               navigate("/");
          //             } else {
          //               console.log(
          //                 "Navigating to onboarding due to userStatus:",
          //                 data.data.status,
          //               );
          //               navigate("/onboarding");
          //             }
          //           }
        }
        if (data.event === 'mobile_upload_complete') {
          dispatch(
            setFileDetails({
              verification_id: null,
              value: data.data,
              status: 'pending',
              rejection_reason: null,
              rejection_comment: null,
              rejection_timestamp: null,
              rejected_by: null,
              comments: [],
            }),
          );
        }
      } catch (error) {
        showToast(error.message, 'error');
      }
    };

    socket.onerror = (error) => {
      showToast(error.message, 'error');
    };

    socket.onclose = () => {
      console.log('WebSocket disconnected');
    };

    return () => {
      socket.close();
    };
  }, [userId]); // Re-run effect if userId changes

  return (
    <div className="">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {showRoleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-md px-4">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4">
            {/* HEADER */}
            <div className="p-6 border-b bg-gradient-to-r from-[#375AE7]/10 to-indigo-50">
              <h2 className="text-lg font-semibold text-gray-900">
                Choose Your Role
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                You have multiple roles. Select how you want to continue.
              </p>
            </div>

            {/* BODY */}
            <div className="p-4 space-y-3 max-h-[50vh] overflow-y-auto">
              {availableRoles.map((role) => {
                const config = roleConfig[role.name] || roleConfig.customer;
                const Icon = config.icon;

                return (
                  <button
                    key={role.name}
                    onClick={(e) => {
                      setShowRoleModal(false);
                      setLoading(true);
                      handleUpdateSwtich(e, role.name, role.id);
                      //proceedAfterLogin(loginResponse, role.name);
                    }}
                    className={`
                w-full flex items-center justify-between
                p-4 rounded-xl border
                transition-all duration-200
                hover:shadow-md hover:scale-[1.01]
                ${config.bg} ${config.border}
              `}
                  >
                    {/* LEFT SIDE */}
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg bg-white shadow-sm`}>
                        <Icon className={`w-5 h-5 ${config.color}`} />
                      </div>

                      <div className="text-left">
                        <p className="font-semibold text-gray-800 capitalize">
                          {config.label}
                        </p>
                        <p className="text-xs text-gray-500">
                          Continue as {config.label.toLowerCase()}
                        </p>
                      </div>
                    </div>

                    {/* RIGHT */}
                    <CheckCircle2 className={`w-5 h-5 ${config.color}`} />
                  </button>
                );
              })}
            </div>

            {/* FOOTER */}
            <div className="p-4 border-t bg-gray-50">
              <button
                onClick={() => setShowRoleModal(false)}
                className="w-full text-sm text-gray-600 hover:text-gray-900 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebSocketComponent;
